import { apiMunzen } from '@/app/api/apiMunzen';
import type { Feature, MarkMultipleViewedResponse } from '../types';

const FEATURES_BASE_URL = '/features';

/**
 * Obtiene todos los features pendientes para el usuario autenticado
 * @author Omar Sánchez Martínez
 * @since 2025-11-28
 * @returns Promise<Feature[]>
 * @throws Error si hay un error al obtener los features
 */
export const getPendingFeaturesService = async (): Promise<Feature[]> => {
  const response = await apiMunzen.get<{ data: Feature[] }>(`${FEATURES_BASE_URL}/pending`);
  return response.data.data;
};

/**
 * Obtiene features pendientes para un módulo específico
 * @author Omar Sánchez Martínez
 * @since 2025-11-28
 * @param module - Módulo para el que obtener los features
 * @returns Promise<Feature[]> - Lista de features pendientes para el módulo
 * @throws Error si hay un error al obtener los features
 */
export const getPendingFeaturesByModuleService = async (module: string): Promise<Feature[]> => {
  const response = await apiMunzen.get<{ data: Feature[] }>(
    `${FEATURES_BASE_URL}/pending/${module}`,
  );
  return response.data.data;
};

/**
 * Marca un feature como visto
 * @author Omar Sánchez Martínez
 * @since 2025-11-28
 * @param featureKey - Clave del feature a marcar como visto
 * @returns Promise<void>
 * @throws Error si hay un error al marcar el feature como visto
 */
export const markFeatureAsViewedService = async (featureKey: string): Promise<void> => {
  await apiMunzen.post(`${FEATURES_BASE_URL}/${featureKey}/viewed`);
};

/**
 * Marca múltiples features como vistos (batch)
 * @author Omar Sánchez Martínez
 * @since 2025-11-28
 * @param featureKeys - Claves de los features a marcar como vistos
 * @returns Promise<MarkMultipleViewedResponse>
 * @throws Error si hay un error al marcar los features como vistos
 */
export const markMultipleFeaturesAsViewedService = async (
  featureKeys: string[],
): Promise<MarkMultipleViewedResponse> => {
  const response = await apiMunzen.post<MarkMultipleViewedResponse>(`${FEATURES_BASE_URL}/viewed`, {
    feature_keys: featureKeys,
  });
  return response.data;
};
